import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

function isSameDay(a: Date, b: Date | null): boolean {
  if (!b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Átomo: carrusel horizontal de días para seleccionar fecha.
 * Muestra una ventana de N días con navegación prev/next y un encabezado de mes.
 */
@Component({
  selector: 'ui-day-strip',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './day-strip.component.html',
  styleUrl: './day-strip.component.scss',
  host: { class: 'ui-day-strip-host' },
})
export class UiDayStripComponent {
  /** Fecha actualmente seleccionada (controlada por el padre) */
  readonly selectedDate = input<Date | null>(null);

  /** Número de días visibles en la ventana */
  readonly windowSize = input<number>(5);

  readonly daySelect = output<Date>();

  /** Offset en pasos de windowSize desde hoy */
  protected readonly windowStep = signal(0);

  protected readonly visibleDays = computed<Date[]>(() => {
    const today = startOfDay(new Date());
    const size = this.windowSize();
    const step = this.windowStep();
    return Array.from({ length: size }, (_, i) =>
      addDays(today, step * size + i),
    );
  });

  protected readonly headerLabel = computed(() => {
    const days = this.visibleDays();
    if (!days.length) return '';
    const first = days[0];
    const last = days[days.length - 1];
    const month = MONTH_NAMES[first.getMonth()];
    const year = first.getFullYear();
    if (first.getMonth() !== last.getMonth()) {
      return `${month} / ${MONTH_NAMES[last.getMonth()]} ${year}`;
    }
    return `${month} ${year}`;
  });

  protected prev(): void {
    this.windowStep.update((s) => s - 1);
  }

  protected next(): void {
    this.windowStep.update((s) => s + 1);
  }

  protected select(date: Date): void {
    this.daySelect.emit(date);
  }

  protected dayName(date: Date): string {
    return DAY_NAMES[date.getDay()];
  }

  protected dayNumber(date: Date): number {
    return date.getDate();
  }

  protected isSelected(date: Date): boolean {
    return isSameDay(date, this.selectedDate());
  }

  protected isToday(date: Date): boolean {
    return isSameDay(date, startOfDay(new Date()));
  }
}
